import { createTheme, type Theme } from '@mui/material/styles';
import { mixWithWhite } from './environment';
import {
  accent,
  action,
  border,
  contentType,
  contentTypeDark,
  diff,
  elevation,
  fontFamily,
  fontSize,
  radius,
  space,
  stage,
  surface,
  text,
  type ModeValue,
  type StageKey,
  type ThemeMode,
} from './tokens';

/** The app's two MUI themes, built from `tokens.ts`. */

/** One mode's half of a token that carries both, or the token itself. */
const resolve = (value: ModeValue, mode: ThemeMode): string =>
  typeof value === 'string' ? value : value[mode];

/**
 * **4px, not MUI's 8px**, so the design's 12 / 16 / 24 / 32 / 48 scale keeps its
 * 3 / 4 / 6 / 8 / 12 names and a token name *is* its multiplier. The trap it
 * buys: `<Stack spacing={2}>` is 8px here, so anything copied from MUI's docs is
 * half the gap it expects.
 */
const SPACING_BASE = 4;

/**
 * The stage palette, keyed the way records are. A record's `stage` carries the
 * API's casing, from `services/content/utils/stages.ts`, and `stageKeyFor` is
 * the one place that becomes a palette key — so no component lowercases a
 * string to index a colour.
 */
const STAGE_KEYS: Record<string, StageKey> = {
  Delivery: 'delivery',
  Discovery: 'discovery',
  Design: 'design',
  Build: 'build',
  Run: 'run',
};

/**
 * The palette key for an API stage name, or **`undefined` rather than a fallback
 * colour** — an unrecognised stage is a taxonomy change, and the caller decides
 * whether to omit the decoration or render a neutral one.
 */
export const stageKeyFor = (stageName: string): StageKey | undefined =>
  STAGE_KEYS[stageName];

/**
 * Which of a stage colour's two jobs a caller is doing: `fill` is a solid ground
 * carrying white `text.onBrand`, `onSurface` is ink drawn on the page.
 */
export type StageRole = 'fill' | 'onSurface';

/**
 * The palette *path* for a stage's colour in a given role — `stage.design.fill`
 * — or `text.secondary` for a stage we do not know.
 *
 * **A path rather than a hex**, so `sx` resolves it against whichever theme is
 * mounted and the component never learns what mode it is in.
 *
 * **Every component that paints itself by stage goes through here**, so an
 * unrecognised stage is plainly grey rather than plausibly coloured, and the
 * role choice is made once per call site rather than by accident — see
 * `docs/adr/0001-one-colour-per-stage.md`.
 */
export const stageColourFor = (stageName: string, role: StageRole): string => {
  const key = stageKeyFor(stageName);

  return key === undefined ? 'text.secondary' : `stage.${key}.${role}`;
};

/**
 * The resolved hex, for the one place `sx` cannot resolve a path: a colour
 * inside a template string. Same unknown-stage rule as `stageColourFor`, so
 * that caller does not reimplement it.
 */
export const stageColourValue = (theme: Theme, stageName: string, role: StageRole): string => {
  const key = stageKeyFor(stageName);

  return key === undefined ? theme.palette.text.secondary : theme.palette.stage[key][role];
};

/**
 * The display label for a stage. **`Discovery` renders as `DISCOVER`**, which is
 * a literal string in the designs rather than a transform of the data —
 * uppercasing would give the wrong word, so the mapping is explicit.
 */
const STAGE_LABELS: Record<StageKey, string> = {
  delivery: 'Delivery',
  discovery: 'DISCOVER',
  design: 'DESIGN',
  build: 'BUILD',
  run: 'RUN',
};

export const stageLabelFor = (stageName: string): string => {
  const key = stageKeyFor(stageName);
  return key === undefined ? stageName : STAGE_LABELS[key];
};

/**
 * The same names in sentence case, for anywhere a stage is read rather than
 * shouted.
 *
 * **Two maps rather than one uppercased**, because they disagree on more than
 * case: `Delivery` is title case in `STAGE_LABELS` while its four siblings are
 * not, so deriving one from the other would change the rail's Delivery bar.
 */
const STAGE_NAMES: Record<StageKey, string> = {
  delivery: 'Delivery',
  discovery: 'Discover',
  design: 'Design',
  build: 'Build',
  run: 'Run',
};

export const stageNameFor = (stageName: string): string => {
  const key = stageKeyFor(stageName);
  return key === undefined ? stageName : STAGE_NAMES[key];
};

/**
 * How a content type's badge paints itself: its text colour and its ground.
 *
 * **A palette lookup rather than a derivation**, because only light's ground is
 * derivable. An unknown type comes back neutral, for the same reason
 * `stageColourFor` does.
 */
export const contentTypeColourFor = (
  palette: Theme['palette'],
  type: string,
): { colour: string; surface: string } => {
  const badge = (palette.contentType as Record<string, { colour: string; surface: string } | undefined>)[
    type
  ];

  return badge ?? { colour: palette.text.meta, surface: palette.surface.sunken };
};

/**
 * The three badges for one mode, built once into the palette so
 * `contentTypeColourFor` stays a lookup and no caller has to know the mode.
 */
const contentTypeBadgesFor = (
  mode: ThemeMode,
): Record<keyof typeof contentType, { colour: string; surface: string }> =>
  mode === 'light'
    ? {
        Phase: { colour: contentType.Phase, surface: mixWithWhite(contentType.Phase, 0.9) },
        Process: { colour: contentType.Process, surface: mixWithWhite(contentType.Process, 0.9) },
        Method: { colour: contentType.Method, surface: mixWithWhite(contentType.Method, 0.9) },
      }
    : contentTypeDark;

export const createAppTheme = (mode: ThemeMode): Theme =>
  createTheme({
    spacing: SPACING_BASE,

    /**
     * Mobile / tablet / desktop onto `xs` / `sm` / `md`. **The keys are kept
     * because MUI's own components use them** — Container, Grid and Toolbar
     * reference `sm` and `md` internally. `lg` and `xl` are unused.
     */
    breakpoints: {
      values: { xs: 0, sm: 768, md: 1024, lg: 1320, xl: 1440 },
    },

    palette: {
      mode,
      primary: { main: action.primary, contrastText: text.onBrand },
      background: { default: resolve(surface.page, mode), paper: resolve(surface.raised, mode) },
      text: {
        primary: resolve(text.primary, mode),
        secondary: resolve(text.secondary, mode),
        disabled: resolve(text.disabled, mode),
        meta: resolve(text.meta, mode),
        link: resolve(text.link, mode),
        onBrand: text.onBrand,
      },
      divider: resolve(border.default, mode),
      surface: {
        page: resolve(surface.page, mode),
        raised: resolve(surface.raised, mode),
        sunken: resolve(surface.sunken, mode),
      },
      border: {
        default: resolve(border.default, mode),
        strong: resolve(border.strong, mode),
      },
      accent: {
        blueSurface: resolve(accent.blueSurface, mode),
        brandSurface: resolve(accent.brandSurface, mode),
        errorText: resolve(accent.errorText, mode),
        infoSurface: resolve(accent.infoSurface, mode),
        info: resolve(accent.info, mode),
        infoBorder: resolve(accent.infoBorder, mode),
        focusRing: accent.focusRing,
      },
      diff: {
        addedSurface: resolve(diff.addedSurface, mode),
        removedSurface: resolve(diff.removedSurface, mode),
      },
      stage: {
        delivery: { fill: stage.delivery.fill, onSurface: resolve(stage.delivery.onSurface, mode) },
        discovery: {
          fill: stage.discovery.fill,
          onSurface: resolve(stage.discovery.onSurface, mode),
        },
        design: { fill: stage.design.fill, onSurface: resolve(stage.design.onSurface, mode) },
        build: { fill: stage.build.fill, onSurface: resolve(stage.build.onSurface, mode) },
        run: { fill: stage.run.fill, onSurface: resolve(stage.run.onSurface, mode) },
      },
      contentType: contentTypeBadgesFor(mode),
    },

    shape: { borderRadius: radius.md },

    typography: {
      fontFamily: fontFamily.body,

      /**
       * **The design's names are not MUI's**, mapped rather than renamed so MUI's
       * own components keep working and `h1` is still the biggest thing on a page.
       *
       * | Design | px | MUI |  | Design | px | MUI |
       * | --- | --- | --- | --- | --- | --- | --- |
       * | display | 32 | `h1` |  | body | 16 | `body1` |
       * | h1 | 24 | `h2` |  | body-sm | 14 | `body2` |
       * | h2 | 20 | `h3` |  | label | 12 | `caption` / `overline` |
       *
       * `h4` to `h6` (16 / 14 / 13) are card and section titles. The designs set
       * those per card, so they carry no token — they are here so a card title is
       * Poppins without every card saying so.
       */
      h1: { fontFamily: fontFamily.heading, fontSize: fontSize.display, fontWeight: 700, lineHeight: 1.2 },
      h2: { fontFamily: fontFamily.heading, fontSize: fontSize.h1, fontWeight: 600, lineHeight: 1.3 },
      h3: { fontFamily: fontFamily.heading, fontSize: fontSize.h2, fontWeight: 600, lineHeight: 1.4 },
      h4: { fontFamily: fontFamily.heading, fontSize: fontSize.body, fontWeight: 600, lineHeight: 1.4 },
      h5: { fontFamily: fontFamily.heading, fontSize: fontSize.bodySm, fontWeight: 600, lineHeight: 1.4 },
      h6: { fontFamily: fontFamily.heading, fontSize: 13, fontWeight: 600, lineHeight: 1.4 },

      body1: { fontSize: fontSize.body, lineHeight: 1.6 },
      body2: { fontSize: fontSize.bodySm, lineHeight: 1.55 },
      caption: { fontSize: fontSize.label, lineHeight: 1.4 },

      /** The eyebrow above a title — "METHOD 3.1.1 · LEVEL 3 METHOD". */
      overline: {
        fontSize: fontSize.label,
        fontWeight: 500,
        lineHeight: 1.4,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      },

      /** No shouting. MUI uppercases button labels by default; the designs do not. */
      button: { fontSize: fontSize.bodySm, fontWeight: 500, textTransform: 'none' },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: resolve(surface.page, mode),
            color: resolve(text.primary, mode),
            // What makes the browser paint its own chrome — scrollbars, native
            // form controls, the canvas behind the app — to match.
            colorScheme: mode,
            // Retina rendering closer to the Figma frames, which are rendered by
            // a browser doing the same thing.
            WebkitFontSmoothing: 'antialiased',
          },
          /**
           * **One focus ring for the whole app, and it is `:focus-visible`.**
           * `:focus` would ring every mouse click; leaving it to the browser
           * gives five different rings across the components below.
           */
          '*:focus-visible': {
            outline: `2px solid ${accent.focusRing}`,
            outlineOffset: 2,
          },
        },
      },

      /** Two shadows and no more — see `elevation` in the tokens. */
      MuiPaper: {
        styleOverrides: {
          elevation1: { boxShadow: elevation[1] },
          elevation2: { boxShadow: elevation[2] },
        },
      },

      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: radius.md, paddingInline: space[4] },
        },
      },

      MuiLink: {
        defaultProps: { underline: 'hover' },
        styleOverrides: { root: { color: resolve(text.link, mode) } },
      },
    },
  });

/**
 * Both themes, built once at module load rather than behind a memoised factory.
 * The mode set is closed and tiny, so a `Record` lookup gives what a per-toggle
 * `createTheme()` would — and keeps the reference stable for Emotion's cache.
 */
export const themes: Record<ThemeMode, Theme> = {
  light: createAppTheme('light'),
  dark: createAppTheme('dark'),
};
